/**
 * Register a UI element in the fabric registry for its mounted lifetime and
 * return the `data-guide-id` prop to spread onto the real DOM element — ONE
 * call wires both the registry entry and the spotlight target convention.
 */
import { useEffect } from 'react'

import { useFabricRegistryStore, type RegistryEntry } from '@/stores/fabricRegistry'

/**
 * Declare the fabric surface this page IS for its mounted lifetime — the
 * surface_id the ui_context manifest attributes registered entries to. One
 * call per page component; the single owner of the convention the previously
 * copy-pasted inline setSurface effects carried.
 */
export function useSurface(surfaceId: string): void {
  useEffect(() => {
    useFabricRegistryStore.getState().setSurface(surfaceId)
  }, [surfaceId])
}

export function useGuideRegister(
  entry: RegistryEntry,
  activate?: () => void,
  enabled = true,
): { 'data-guide-id': string } {
  const register = useFabricRegistryStore((s) => s.register)
  // The effect runs every render (hooks rules), but only registers while
  // `enabled` is true — so the registry advertises the entry ONLY while the
  // element it describes is actually mounted (e.g. a button rendered behind a
  // condition). When `enabled` flips true→false, React runs the previous
  // effect's unregister cleanup first, so the stale entry is removed.
  useEffect(() => {
    if (!enabled) return
    return register(
      {
        guideId: entry.guideId,
        surfaceId: entry.surfaceId,
        kind: entry.kind,
        labelKey: entry.labelKey,
      },
      activate,
    )
  }, [register, entry.guideId, entry.surfaceId, entry.kind, entry.labelKey, activate, enabled])
  return { 'data-guide-id': entry.guideId }
}
